/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/ban-ts-comment */

// https://github.com/typescript-eslint/typescript-eslint/issues/2449
import * as React from 'react';
import * as redux from 'redux';
import { Features } from '@tock/coretypes';
import { actions } from 'actions';
import { connect, useDispatch } from 'react-redux';
import _ from 'lodash';
import rootReducer from 'state';

type ArgumentTypes<F> = F extends (...args: infer A) => any ? A : never;
type VoidFunction<T> = (...args: ArgumentTypes<T>) => void;
type PromiseFunction<T, U> = (...args: ArgumentTypes<T>) => Promise<U>;
export type PromiseProtoFunction<T, P> = (...args: ArgumentTypes<T>) => Promise<ProtoResponse<P>>;

// Action Types
type MapApiAction<T> = T extends (...args: any[]) => ApiAction<infer P>
  ? PromiseProtoFunction<T, P>
  : T extends (...args: any[]) => Promise<infer U>
    ? PromiseFunction<T, U>
    : VoidFunction<T>;

export type ActionsFile<T> = { [P in keyof T]: MapApiAction<T[P]> };
export type MappedActions<T> = { [P in keyof T]: ActionsFile<T[P]> };

// Store Types
type ActionsFileBundle = typeof actions;
export type ActionsType = MappedActions<ActionsFileBundle>;

export type StoreType = ReturnType<typeof rootReducer>;

export interface Store {
  getState(): StoreType;
  dispatch(action: ApiAction | BaseTockAction): void;
}

const getActions = (dispatch: Dispatch): ActionsType => {
  // 2019-04-19 km: can't see how to use enums as action type here
  return _.mapValues(actions, (actionsSet: any) => {
    return redux.bindActionCreators(actionsSet, dispatch);
  }) as any; // TODO: fixme
};

let actionsCache: {
  actions: ActionsType;
};

const getActionsMemoized = (dispatch: Dispatch): { actions: ActionsType } => {
  if (actionsCache) {
    return actionsCache;
  }

  actionsCache = {
    actions: getActions(dispatch),
  };
  return actionsCache;
};

function mapDispatchToProps<RequiredProps, Actions>(dispatchMapper?: any) {
  return (dispatch: any, props: RequiredProps) => {
    const actionsOverride = (props as any).actions;
    if (!dispatchMapper && !actionsOverride) {
      return getActionsMemoized(dispatch);
    }

    let customDispatch = dispatchMapper || {};
    if (_.isFunction(dispatchMapper)) {
      customDispatch = dispatchMapper(dispatch, props);
    }

    return {
      actions: {
        ...getActionsMemoized(dispatch).actions,
        ...customDispatch,
        ...(actionsOverride || {}),
      },
    };
  };
}

export const useActions = <RequiredProps = {}, AdditionalActions extends {} = {}>(opts?: {
  dispatchMapper?: (dispatch: Dispatch, props?: RequiredProps) => AdditionalActions;
  props?: RequiredProps;
}) => {
  const dispatchMapper = opts && opts.dispatchMapper;
  const props = opts && opts.props;

  const dispatch = useDispatch();
  if (!dispatchMapper && !props) {
    // @ts-ignore-error typing issue with redux 8
    return getActionsMemoized(dispatch) as { actions: ActionsType };
  }

  const actionsOverride = (props as any).actions;

  let customDispatch = dispatchMapper || {};
  if (_.isFunction(dispatchMapper)) {
    // @ts-ignore-error typing issue with redux 8
    customDispatch = dispatchMapper(dispatch, props);
  }

  return {
    actions: {
      // @ts-ignore-error typing issue with redux 8
      ...getActionsMemoized(dispatch).actions,
      ...customDispatch,
      ...(actionsOverride || {}),
    } as ActionsType & AdditionalActions,
  };
};

type StoreKeys = Array<keyof StoreType>;
type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;
type SelectorFnReturnType<Props> = Omit<Props, keyof DefaultConnectProps>;
type SelectorFn<RequiredProps, PickedConnectProps> = (
  state: StoreType,
  ownProps: RequiredProps
) => SelectorFnReturnType<PickedConnectProps>;

export interface DefaultConnectProps {
  actions: ActionsType;
  app: StoreType['app'];
  features: Features;
  globalFeatures: Features;
  locale: string;
  currency: string;
  timeZone: string;
}

type ConnectableComponent<T> =
  | React.ComponentClass<T>
  | React.FunctionComponent<React.PropsWithChildren<T>>;
interface IntrinsicAttributes {
  children?: React.ReactNode;
  ref?: string;
}

export default function <
  RequiredProps = {},
  PickedConnectProps extends DefaultConnectProps = DefaultConnectProps,
  AdditionalActions = {},
>(
  component: ConnectableComponent<
    RequiredProps & PickedConnectProps & { actions: ActionsType & AdditionalActions }
  >,
  selector: StoreKeys | SelectorFn<RequiredProps, PickedConnectProps> = [] as StoreKeys,
  dispatchMapperExternal?: (dispatch: Dispatch) => AdditionalActions,
  shouldComponentUpdate?: (propsFromParent: RequiredProps) => boolean
): React.NamedExoticComponent<RequiredProps & IntrinsicAttributes> {
  const connector = connect((state: StoreType, propsFromParent: RequiredProps) => {
    let prevProps!: any;
    return (state: StoreType, propsFromParent: RequiredProps) => {
      if (prevProps && shouldComponentUpdate && !shouldComponentUpdate(propsFromParent)) {
        return prevProps;
      }
      const newProps = {
        app: state.app,
        features: state.features.features,
        globalFeatures: state.features.globalFeatures ?? {},
        locale: (propsFromParent as any).locale || state.app.locale,
        currency: (propsFromParent as any).currency || state.app.currency,
        timeZone: (propsFromParent as any).timeZone || state.app.timeZone,
        ...(_.isArray(selector) ? _.pick(state, selector) : selector(state, propsFromParent)),
      };

      prevProps = newProps;
      return newProps;
    };
  }, mapDispatchToProps<RequiredProps, ActionsType & AdditionalActions>(dispatchMapperExternal));

  // @ts-ignore-error typing issue with redux 8
  return connector(component as any) as React.NamedExoticComponent<
    RequiredProps & IntrinsicAttributes
  >;
}
